<template>
	<div class="watch-readings-details-widget widget-latest">
		<div class="header">
			<h2>{{ $t('readings.general.latest') }}</h2>
		</div>
		<div class="body">
			<h1>{{ readingType }}</h1>
			<spinner v-if="loading" class="spinner"/>
			<div v-else-if="readings.length" class="widget-table" :class="{ falls: isFalls }">
				<div class="widget-table-header">
					<div v-if="!isFalls">{{ $t('labels.general.value') }}</div>
					<div>{{ $t('labels.general.date') }}</div>
					<div v-if="isFalls">{{ $t('labels.general.ok') }}</div>
					<div v-if="isFalls">{{ $t('labels.general.sos') }}</div>
					<div v-if="isFalls">{{ $t('labels.general.false_positive') }}</div>
					<div v-if="isFalls">{{ $t('labels.general.no_response') }}</div>
					<div></div>
				</div>
				<div class="widget-table-body">
					<latest-row
						v-for="(item, index) in readings"
						v-model="readings[index]"
						:key="item.id"
						:units="units"
						:class="{ active: isActive(item.id) }"
						class="widget-table-row"
						@click.native="onClick(item)"/>
					<div class="loading-more">
						<button-loading
							v-show="!finished"
							:loading="loading_more"
							@click.native="getMoreReadings"
							ref="button"
							class="btn btn-small btn-round btn-primary btn-loading">
							{{ $t('buttons.actions.get_more') }}
						</button-loading>
					</div>
				</div>
			</div>
			<div v-else class="no-results">{{ $t('readings.general.no_readings') }}</div>
		</div>
		<button @click="refresh" class="btn btn-icon btn-refresh mobile">&nbsp;</button>
		<modal-box-biometric v-if="isBiometric" :user_uid="user_uid" :watch_id="watch_id"/>
		<modal-box-geofencing v-if="isGeofencing"/>
	</div>
</template>

<script>
import uniqueid from '@/utils/uniqueid'
import Readings from '@/utils/readings'
import Reading from '@/classes/Reading'
import ReadingFalls from '@/classes/ReadingFalls'
import ReadingHeartRate from '@/classes/ReadingHeartRate'
import LatestRow from './rows/ReadingsWidgetLatestRow'
import ModalBoxBiometric from './modal/ModalBoxBiometric'
import ModalBoxGeofencing from './modal/ModalBoxGeofencing'
export default {
	components: {
		LatestRow,
		ModalBoxBiometric,
		ModalBoxGeofencing
	},
	props: [
		'user_uid',
		'watch_id',
		'type_id',
		'refresh'
	],
	data () {
		return {
			name: 'watch biometric latest',
			loadingSet: new Set(),
			loadingTracker: 1,
			readings: [],
			active_id: null,
			loading_more: false,
			finished: false,
			limit: 10,
			observer: null
		}
	},
	computed: {
		units () {
			return this.type_id ? this.$t(Readings.readingTypes.get(parseInt(this.type_id)).translationKey + '.html') : '(no type)'
		},
		readingType () {
			return Readings.readingTypes.get(parseInt(this.type_id)).name
		},
		loading () {
			let temp = this.loadingTracker // Hack, force Vue computing loading
			return this.loadingSet.size > 0
		},
		isGeofencing () {
			return this.type_id === Readings.TYPE_GEOFENCING
		},
		isBiometric () {
			return this.type_id !== Readings.TYPE_GEOFENCING
		},
		isFalls () {
			return this.readings.length ? this.readings[0].type_id === Readings.TYPE_FALLS : false
		},
		offset () {
			return this.readings.length
		}
	},
	methods: {
		isActive (id) {
			return this.active_id === id
		},
		onClick (reading) {
			this.active_id = reading.id
			// Emit signal to modal.
			this.$emit('open-modal', reading)
		},
		getReadings () {
			const uid = uniqueid()
			this.loadingSet.add(uid)
			this.loadingTracker++ // Hack, force Vue computing loading
			this.readings = []
			this.finished = false
			this.$api.readings.getAllByType(this, this.user_uid, this.watch_id, this.type_id, this.offset, this.limit)
				.then(response => {
					this.$log.info(this.name, 'readings.getAllByType OK', response)()
					let readings = []
					response.body.forEach(element => {
						switch (this.type_id) {
							case Readings.TYPE_FALLS:
								readings.push(new ReadingFalls(element))
								break
							default:
								readings.push(new Reading(element))
						}
					})
					this.readings = readings
				}).catch(error => {
					this.$log.error(this.name, 'readings.getAllByType ERROR', error)()
					this.error = error
				}).finally(() => {
					this.loadingSet.delete(uid)
					this.loadingTracker++ // Hack, force Vue computing loading
				})
		},
		getMoreReadings () {
			this.loading_more = true
			this.$api.readings.getAllByType(this, this.user_uid, this.watch_id, this.type_id, this.offset, this.limit)
				.then(response => {
					this.$log.info(this.name, 'readings.getAllByType OK', response)()
					let readings = []
					response.body.forEach(element => {
						readings.push(new Reading(element))
					})
					if (readings.length) {
						this.readings = this.readings.concat(readings)
					}
					if (!readings.length || readings.length < this.limit) {
						this.finished = true
					}
				}).catch(error => {
					this.$log.error(this.name, 'readings.getAllByType ERROR', error)()
					this.error = error
				}).finally(() => {
					this.loading_more = false
				})
		},
		handleIntersect (entry) {
			if (entry[0].isIntersecting && !this.loading_more) {
				this.getMoreReadings()
			}
		},
		createObserver (element) {
			// not use the auto loading when in mobile
			// const options = {
			// 	threshold: 1
			// }
			// this.observer = new IntersectionObserver(this.handleIntersect, options)
			// this.observer.observe(element)
		},
		onRouteChanged () {
			// Next tick, otherwise type_id is not correct
			this.$nextTick(function () {
				this.getReadings()
			})
		}
	},
	created () {
		this.$parent.$on('biometric-details-route-changed', this.onRouteChanged)
		this.$parent.$on('biometric-details-refresh-readings', this.onRouteChanged)
	},
	mounted () {
		this.getReadings()
	},
	updated () {
		this.$nextTick(function () {
			if (this.$refs.button && this.$refs.button.$el) {
				this.createObserver(this.$refs.button.$el)
			}
		})
	},
	beforeDestroy () {
		this.$parent.$off('biometric-details-route-changed', this.onRouteChanged)
		this.$parent.$off('biometric-details-refresh-readings', this.onRouteChanged)
	},
}
</script>



// WEBPACK FOOTER //
// src/components/watch/readings/shared/widgets/ReadingsWidgetLatest.vue